import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { sanitizeInput } from '../../utils/inputValidation';
import { getPrendasListing } from '../../services/prendasService';
import Swal from 'sweetalert2';

const PrendasDisponibles = ({ user, onLogout, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatearFecha = (fecha) => {
    if (!fecha || fecha === 'N/A') return 'N/A';

    try {
      if (fecha.includes('T')) {
        fecha = fecha.split('T')[0];
      }

      if (fecha.includes('-')) {
        const partes = fecha.split('-');

        if (partes.length === 3 && partes[0].length === 4) {
          return `${partes[2]}/${partes[1]}/${partes[0]}`;
        }
      }

      return fecha;
    } catch {
      return fecha;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const response = await getPrendasListing('DISPONIBLES', user.ce_ID);

        if (!response || !Array.isArray(response)) {
          throw new Error('La respuesta del servidor no es válida');
        }

        const mappedData = response.map((item, index) => ({
          id: item.id || index + 1,
          group: item.group || item.grupo || 'N/A',
          order: item.order || item.orden || 'N/A',
          titular: item.titular || item.mainName || 'N/A',
          ident: item.ident || item.documentNumber || 'N/A',
          sellerHouse:
            item.sellerHouse?.ceName ||
            item.sellerHouseName ||
            item.concesionario ||
            'N/A',
          availableDate: formatearFecha(
            item.availableDate ||
            item.recordDate ||
            item.fechaDisponible ||
            'N/A'
          ),

          // Guardamos el objeto completo para pasarlo al formulario
          _raw: item,
        }));

        setData(mappedData);
      } catch (err) {
        console.error('Error cargando prendas disponibles:', err);

        Swal.fire({
          icon: 'error',
          title: 'Error al cargar datos',
          text: `No se pudieron cargar las prendas disponibles: ${err.message}`,
          confirmButtonColor: '#ef4444',
        });

        setData([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const filteredData = data.filter((row) => {
    const s = searchTerm.toLowerCase();

    return (
      String(row.group).toLowerCase().includes(s) ||
      String(row.order).toLowerCase().includes(s) ||
      String(row.titular).toLowerCase().includes(s) ||
      String(row.ident).toLowerCase().includes(s) ||
      String(row.sellerHouse).toLowerCase().includes(s) ||
      String(row.availableDate).toLowerCase().includes(s)
    );
  });

  const handleAgregar = (row) => {
    const prendaData = {
      id: row.id,
      group: row.group,
      order: row.order,
      titular: row.titular,
      ident: row.ident,
      sellerHouse: row.sellerHouse,
      availableDate: row.availableDate,
      fromDisponibles: true,
    };

    localStorage.setItem(
      'prendaData',
      JSON.stringify(prendaData)
    );

    onNavigate('agregarPrenda');
  };

  return (
    <MainLayout
      user={user}
      onLogout={onLogout}
      onNavigate={onNavigate}
    >
      <div className="max-w-full mx-auto px-3 py-4">
        {/* Encabezado */}
        <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>

              <h2 className="text-2xl font-bold text-gradient">
                Prendas Disponibles
              </h2>
            </div>

            <p className="text-gray-500 text-sm ml-5">
              Administre las prendas disponibles pendientes de registro
            </p>
          </div>
        </div>

        {/* Buscador */}
        <div className="mb-4 w-full">
          <div className="relative w-full max-w-2xl">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center">
              <span className="material-icons text-gray-400 text-lg">
                search
              </span>
            </span>

            <input
              type="text"
              placeholder="Buscar por Grupo, Orden, Titular, Ident, Concesionario o F. Creación Prenda..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(sanitizeInput(e.target.value))
              }
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-gray-700 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
            />
          </div>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12">
            <HondaLoader
              message="Cargando prendas disponibles..."
              variant="inline"
              size="large"
              subtitle="Obteniendo datos del servidor"
            />
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-red-600 to-red-700">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Grupo
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Orden
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Titular
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      DNI/CUIT
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Fecha Disponible
                    </th>

                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Concesionario
                    </th>

                    <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        {searchTerm
                          ? 'No se encontraron resultados para la búsqueda'
                          : 'No hay prendas disponibles'}
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((row) => (
                      <tr
                        key={row.id}
                        className="hover:bg-red-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {row.group}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.order}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.titular}
                        </td>

                        <td className="px-4 py-3 text-sm font-mono text-gray-700">
                          {row.ident}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.availableDate}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900">
                          {row.sellerHouse}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAgregar(row);
                            }}
                            className="text-green-600 hover:bg-green-600 hover:text-white transition-colors p-1 rounded-full"
                            title="Agregar prenda"
                          >
                            <span className="material-icons text-xl">
                              arrow_forward
                            </span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                Mostrando {filteredData.length} de {data.length} prendas
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PrendasDisponibles;